import React from 'react';
import { useTranslation } from 'react-i18next';
import './password-strength.scss';

interface PasswordStrengthProps {
    /** Current password value — the meter renders nothing while it is empty. */
    value: string;
}

/**
 * Read-only strength meter for the sign-up / reset password fields.
 *
 * The four rules mirror `validatePassword()` in utils/registration-validation
 * (min 8 chars, one lowercase, one uppercase, one number) so the checklist can
 * never claim a password is fine while the validator rejects it.
 */
export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ value }) => {
    const { t } = useTranslation();

    if (!value) return null;

    const rules = [
        { key: 'PASSWORD_RULE_LENGTH', met: value.length >= 8 },
        { key: 'PASSWORD_RULE_UPPERCASE', met: /[A-Z]/.test(value) },
        { key: 'PASSWORD_RULE_LOWERCASE', met: /[a-z]/.test(value) },
        { key: 'PASSWORD_RULE_NUMBER', met: /\d/.test(value) },
    ];

    const score = rules.filter((rule) => rule.met).length;
    const labels = [
        'PASSWORD_STRENGTH_TOO_WEAK',
        'PASSWORD_STRENGTH_WEAK',
        'PASSWORD_STRENGTH_FAIR',
        'PASSWORD_STRENGTH_STRONG',
        'PASSWORD_STRENGTH_EXCELLENT',
    ];

    return (
        <div className={`inv-pw-strength inv-pw-strength--score-${score}`}>
            <div className="inv-pw-strength__head">
                <span className="inv-pw-strength__title">{t('PASSWORD_STRENGTH_LABEL')}</span>
                <span className="inv-pw-strength__verdict">{t(labels[score])}</span>
            </div>

            <div
                className="inv-pw-strength__bars"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={score}
                aria-valuetext={String(t(labels[score]))}
                aria-label={String(t('PASSWORD_STRENGTH_LABEL'))}
            >
                {[1, 2, 3, 4].map((segment) => (
                    <span
                        key={segment}
                        className={`inv-pw-strength__bar${segment <= score ? ' is-filled' : ''}`}
                    />
                ))}
            </div>

            <ul className="inv-pw-strength__rules">
                {rules.map((rule) => (
                    <li
                        key={rule.key}
                        className={`inv-pw-strength__rule${rule.met ? ' is-met' : ''}`}
                    >
                        <span className="inv-pw-strength__tick" aria-hidden="true">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <span>{t(rule.key)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default PasswordStrength;
